import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import CollapsibleSection from "components/Section";
import { Button } from "components/Button";
import TextValue from "components/TitleValue";
import { TWaterSubscriber } from "types/dto";
import { netCall } from "utils/netCall";
import { TResponseCode } from "utils/netCall/types";
import { ManagementApiUrl } from "environment/routeSettings";
import { useURLTools } from "utils/URLtools";
import { getJalaliDate } from "utils/dateTimeTools";

type TFetchState =
  | "unassigned" // the meter has no current installation
  | "loading"
  | "ready" // a real subscriber record came back
  | "notFound" // request succeeded but carried no usable record
  | "error"; // request itself failed

type TProps = {
  /** From meter details: currentInstallation.waterSubscriberId. */
  waterSubscriberId?: string;
  /** Denormalized values already present on the installation, used as fallbacks. */
  identifierId?: string;
  installationDT?: string;
  /** True while the parent meter request is still in flight. */
  isLoading?: boolean;
};

/**
 * Shows the subscriber currently assigned to this meter.
 *
 * GetWaterMeter only returns the subscriber's id, so the display fields are
 * fetched separately from GetWaterSubscriber — the same pattern the subscriber
 * details page uses to render its meter card. Assignment stays on the
 * subscriber page; this card is read-only.
 *
 * A 200 response does not guarantee a subscriber exists: the meter's
 * currentInstallation can point at an id that no longer resolves. The response
 * body is therefore checked for an actual record rather than trusted on status
 * code alone, so an orphaned reference reads as "not found" instead of a card
 * full of blank fields.
 */
export const MeterSubscriberInfo = (props: TProps) => {
  const { waterSubscriberId, identifierId, installationDT, isLoading } = props;

  const { t } = useTranslation();
  const { navigateWithQueryParams } = useURLTools();

  const [data, setData] = useState<TWaterSubscriber>();
  const [state, setState] = useState<TFetchState>("unassigned");

  // Guards against a slower earlier response overwriting a newer one when the
  // assigned subscriber changes while a request is still in flight.
  const requestIdRef = useRef(0);

  const getSubscriberDetails = useCallback(async (subscriberId: string) => {
    const requestId = ++requestIdRef.current;
    setState("loading");
    const today = new Date().toISOString().split("T")[0];
    const res = await netCall<TWaterSubscriber>({
      method: "POST",
      endPoint: `${ManagementApiUrl()}/api/WaterSubscriber/GetWaterSubscriber`,
      body: {
        subscriberId: subscriberId,
        startDT: today,
        endDT: today,
      },
    });
    if (requestId !== requestIdRef.current) return;

    if (res.responseCode !== TResponseCode.OK) {
      // netCall already surfaces the backend's message as a toast.
      setData(undefined);
      setState("error");
      return;
    }

    const record = res.results;
    // An id is the minimum proof of a real record; anything less means the
    // reference is dangling, even though the call returned 200.
    if (!record?.id) {
      setData(undefined);
      setState("notFound");
      return;
    }

    setData(record);
    setState("ready");
  }, []);

  useEffect(() => {
    if (waterSubscriberId) {
      getSubscriberDetails(waterSubscriberId);
    } else {
      // Meter was unassigned while the page was open — drop the stale subscriber.
      requestIdRef.current++;
      setData(undefined);
      setState("unassigned");
    }
  }, [waterSubscriberId, getSubscriberDetails]);

  const renderContent = () => {
    if (state === "unassigned") {
      return (
        <p className={"text-sm text-gray-90"}>{t("noSubscriberAssigned")}</p>
      );
    }

    if (state === "error") {
      return (
        <div className={"w-full flex items-center justify-between gap-x-4"}>
          <span className={"text-sm text-error-80"}>
            {t("errorInGettingSubscriberInfo")}
          </span>
          <Button
            variant={"outlined"}
            icon={"MdRefresh"}
            onClick={() =>
              waterSubscriberId && getSubscriberDetails(waterSubscriberId)
            }
          >
            {t("retry")}
          </Button>
        </div>
      );
    }

    if (state === "notFound") {
      // Surface what the meter itself knows, so the bad reference is actionable.
      return (
        <div className={"w-full flex flex-col gap-y-3"}>
          <span className={"text-sm text-error-80"}>
            {t("subscriberNotFound")}
          </span>
          <div className={"grid grid-cols-2 gap-y-3 gap-x-6 w-full"}>
            <TextValue
              title={t("subscriberId")}
              value={waterSubscriberId}
              className={"col-span-2"}
            />
            {identifierId && (
              <TextValue
                title={t("subscriberIdentifierId")}
                value={identifierId}
                className={"col-span-1"}
              />
            )}
            {installationDT && (
              <TextValue
                title={t("installationDT")}
                value={getJalaliDate({ date: installationDT })}
                className={"col-span-1"}
              />
            )}
          </div>
        </div>
      );
    }

    // "loading" renders the field skeleton under the section's loading overlay;
    // "ready" renders the same markup with values filled in.
    return (
      <div className={"w-full flex flex-col gap-y-3"}>
        <div className={"grid grid-cols-2 gap-y-3 gap-x-6 w-full"}>
          <TextValue
            title={t("subscriberName")}
            value={data?.subscriberName}
            className={"col-span-1"}
          />
          <TextValue
            title={t("subscriberIdentifierId")}
            value={data?.identifierId ?? identifierId}
            className={"col-span-1"}
          />
          <TextValue
            title={t("nationalId")}
            value={data?.nationalId}
            className={"col-span-1"}
          />
          <TextValue
            title={t("mobileNumber")}
            value={data?.mobileNumber}
            className={"col-span-1"}
          />
          {installationDT && (
            <TextValue
              title={t("installationDT")}
              value={getJalaliDate({ date: installationDT })}
              className={"col-span-1"}
            />
          )}
        </div>

        {state === "ready" && (
          <div className={"w-full flex items-center justify-end"}>
            <Button
              variant={"outlined"}
              icon={"MdPerson"}
              onClick={() =>
                navigateWithQueryParams(
                  `/dashboard/subscribers/details/${waterSubscriberId}`,
                  [],
                  { openInNewTab: true }
                )
              }
            >
              {t("goToSubscriberDetails")}
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <CollapsibleSection
      title={t("subscriberInfo")}
      collapsible={true}
      isLoading={isLoading || state === "loading"}
      skeleton="text"
    >
      {renderContent()}
    </CollapsibleSection>
  );
};
